import { PriceHistoryEntry } from '../../models/economy-models';

export interface PathGenerationOptions {
  width: number;
  height: number;
  padding: number;
}

export interface PathSegment {
  path: string;
  color: 'grey' | 'green' | 'red';
  isDotted: boolean;
}

export interface EnhancedPathGenerationOptions extends PathGenerationOptions {
  previousValue?: {
    price: number;
    ts: string;
  };
  isUnique: boolean;
}

/**
 * Generates a smooth SVG path from price history data
 * @param history Price history entries (in reverse chronological order - most recent first)
 * @param options SVG dimensions and padding
 * @returns SVG path string
 */
export function generateValueHistoryPath(
  history: PriceHistoryEntry[],
  options: PathGenerationOptions
): string {
  if (history.length < 2) {
    return '';
  }

  // Reverse array for visualization (oldest to newest, left to right)
  const reversedHistory = [...history].reverse();

  // Extract prices and find min/max for normalization
  const prices = reversedHistory.map(entry => entry.price);
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);
  const priceRange = maxPrice - minPrice;

  // Calculate coordinates
  const width = options.width - (options.padding * 2);
  const height = options.height - (options.padding * 2);
  const xStep = width / (reversedHistory.length - 1);

  const points: { x: number; y: number }[] = reversedHistory.map((entry, index) => {
    const x = options.padding + (index * xStep);
    // Normalize price: min price = bottom, max price = top
    // SVG y-axis: 0 is top, so we invert
    const normalizedPrice = priceRange > 0
      ? (entry.price - minPrice) / priceRange
      : 0.5; // If all prices are the same, center it
    const y = options.padding + (height - (normalizedPrice * height));
    return { x, y };
  });

  // Generate smooth path using cubic bezier curves
  if (points.length === 0) {
    return '';
  }

  if (points.length === 2) {
    // Simple line for just 2 points
    return `M ${points[0].x} ${points[0].y} L ${points[1].x} ${points[1].y}`;
  }

  // Start with first point
  let path = `M ${points[0].x} ${points[0].y}`;

  // Use cubic bezier curves for smoother transitions
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i];

    if (i === 1) {
      // First curve: smooth start with control points that create gentle curve
      const dx = curr.x - prev.x;
      const dy = curr.y - prev.y;
      const controlX1 = prev.x + dx * 0.4;
      const controlY1 = prev.y + dy * 0.4;
      const controlX2 = prev.x + dx * 0.7;
      const controlY2 = prev.y + dy * 0.7;
      path += ` C ${controlX1} ${controlY1}, ${controlX2} ${controlY2}, ${curr.x} ${curr.y}`;
    } else if (i === points.length - 1) {
      // Last curve: smooth end
      const prevPrev = points[i - 2];
      const dx1 = prev.x - prevPrev.x;
      const dy1 = prev.y - prevPrev.y;
      const dx2 = curr.x - prev.x;
      const dy2 = curr.y - prev.y;

      // Control point 1: continues from previous direction
      const controlX1 = prev.x + dx1 * 0.3;
      const controlY1 = prev.y + dy1 * 0.3;
      // Control point 2: smooth transition to end
      const controlX2 = prev.x + dx2 * 0.6;
      const controlY2 = prev.y + dy2 * 0.6;
      path += ` C ${controlX1} ${controlY1}, ${controlX2} ${controlY2}, ${curr.x} ${curr.y}`;
    } else {
      // Middle curves: smooth continuation using Catmull-Rom-like approach
      const prevPrev = points[i - 2];
      const next = points[i + 1];

      // Calculate tangent vectors for smooth curves
      const dx1 = prev.x - prevPrev.x;
      const dy1 = prev.y - prevPrev.y;
      const dx2 = next.x - curr.x;
      const dy2 = next.y - curr.y;

      // Control point 1: extends from previous direction (smooth continuation)
      const controlX1 = prev.x + dx1 * 0.25;
      const controlY1 = prev.y + dy1 * 0.25;

      // Control point 2: anticipates next direction (smooth transition)
      const controlX2 = curr.x - dx2 * 0.25;
      const controlY2 = curr.y - dy2 * 0.25;

      path += ` C ${controlX1} ${controlY1}, ${controlX2} ${controlY2}, ${curr.x} ${curr.y}`;
    }
  }

  return path;
}

/**
 * Generates enhanced SVG path segments with previous value integration
 * @param history Price history entries (in reverse chronological order - most recent first)
 * @param options Enhanced options including previous value data
 * @returns Array of path segments with colors and styles
 */
export function generateEnhancedValueHistoryPath(
  history: PriceHistoryEntry[],
  options: EnhancedPathGenerationOptions
): PathSegment[] {
  // Fallback: If no previous value, use existing logic
  if (!options.previousValue || history.length < 2) {
    const path = generateValueHistoryPath(history, options);
    if (!path) return [];

    // Determine color based on first vs last entry
    const reversedHistory = [...history].reverse();
    const mostRecentPrice = reversedHistory[reversedHistory.length - 1].price;
    const oldestPrice = reversedHistory[0].price;
    const color = mostRecentPrice >= oldestPrice ? 'green' : 'red';

    return [{ path, color, isDotted: false }];
  }

  const previousTs = new Date(options.previousValue.ts).getTime();
  const reversedHistory = [...history].reverse();
  const oldestHistoryTs = new Date(reversedHistory[0].ts).getTime();

  // Scenario A: Previous timestamp is before oldest history timestamp
  if (previousTs < oldestHistoryTs) {
    return generateScenarioAPath(history, options);
  }

  // Scenario B: Previous timestamp is within or after history
  return generateScenarioBPath(history, options);
}

/**
 * Scenario A: Previous value is before oldest history entry
 * Shows: Previous point → dotted line → oldest history point → regular line
 */
function generateScenarioAPath(
  history: PriceHistoryEntry[],
  options: EnhancedPathGenerationOptions
): PathSegment[] {
  const reversedHistory = [...history].reverse();
  const previousValue = options.previousValue!;
  const previousTs = new Date(previousValue.ts).getTime();
  const oldestHistoryTs = new Date(reversedHistory[0].ts).getTime();

  // Calculate dimensions
  const width = options.width - (options.padding * 2);
  const height = options.height - (options.padding * 2);
  const historyLength = reversedHistory.length;

  // Calculate dotted line width (1-3 for currency, 1-2 for uniques)
  const maxDottedUnits = options.isUnique ? 2 : 3;
  const minDottedUnits = 1;
  // Use time gap to determine width, but cap at max
  const timeGap = oldestHistoryTs - previousTs;
  const avgInterval = historyLength > 1
    ? (new Date(reversedHistory[historyLength - 1].ts).getTime() - oldestHistoryTs) / (historyLength - 1)
    : width; // Fallback if we can't calculate (avoid tying to xStep before it exists)
  const proportionalUnits = avgInterval > 0 ? Math.min(maxDottedUnits, Math.max(minDottedUnits, Math.ceil(timeGap / avgInterval))) : minDottedUnits;
  const dottedUnits = Math.floor(proportionalUnits);
  // Partition full width so dotted segment + solid history never exceed `width`
  // (otherwise solid xStep becomes negative and the line moves backward in x).
  const solidIntervals = Math.max(1, historyLength - 1);
  const totalUnits = dottedUnits + solidIntervals;
  const xStep = width / totalUnits;
  const dottedWidth = dottedUnits * xStep;

  // Normalize all prices together for consistent scaling
  const allPrices = [previousValue.price, ...reversedHistory.map(e => e.price)];
  const minPrice = Math.min(...allPrices);
  const maxPrice = Math.max(...allPrices);
  const priceRange = maxPrice - minPrice;

  // Previous point position (at left edge)
  const previousX = options.padding;
  const previousNormalizedPrice = priceRange > 0
    ? (previousValue.price - minPrice) / priceRange
    : 0.5;
  const previousY = options.padding + (height - (previousNormalizedPrice * height));

  // Oldest history point position (after dotted line space)
  const oldestX = options.padding + dottedWidth;
  const oldestNormalizedPrice = priceRange > 0
    ? (reversedHistory[0].price - minPrice) / priceRange
    : 0.5;
  const oldestY = options.padding + (height - (oldestNormalizedPrice * height));

  // Generate dotted line from previous to oldest
  const dottedPath = `M ${previousX} ${previousY} L ${oldestX} ${oldestY}`;

  // Determine color based on previous vs most recent (current) value - use same color for entire line
  const mostRecentPrice = reversedHistory[reversedHistory.length - 1].price;
  const color = previousValue.price <= mostRecentPrice ? 'green' : 'red';

  // Generate history path with offset to account for dotted line space
  const historyPath = generateHistoryPathWithOffset(reversedHistory, options, dottedWidth, minPrice, maxPrice, priceRange, height);

  const segments: PathSegment[] = [
    { path: dottedPath, color, isDotted: true }
  ];

  if (historyPath) {
    // Use same color for entire trendline (previous to current)
    segments.push({ path: historyPath, color, isDotted: false });
  }

  return segments;
}

/**
 * Generate history path with x-offset to account for dotted line space
 */
function generateHistoryPathWithOffset(
  reversedHistory: PriceHistoryEntry[],
  options: PathGenerationOptions,
  offsetX: number,
  minPrice: number,
  maxPrice: number,
  priceRange: number,
  height: number
): string {
  if (reversedHistory.length < 2) return '';

  const width = options.width - (options.padding * 2);
  const availableWidth = width - offsetX;
  const xStep = availableWidth / (reversedHistory.length - 1);

  const points = reversedHistory.map((entry, index) => {
    const x = options.padding + offsetX + (index * xStep);
    const normalizedPrice = priceRange > 0
      ? (entry.price - minPrice) / priceRange
      : 0.5;
    const y = options.padding + (height - (normalizedPrice * height));
    return { x, y };
  });

  if (points.length === 2) {
    return `M ${points[0].x} ${points[0].y} L ${points[1].x} ${points[1].y}`;
  }

  let path = `M ${points[0].x} ${points[0].y}`;

  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i];

    if (i === 1) {
      const dx = curr.x - prev.x;
      const dy = curr.y - prev.y;
      const controlX1 = prev.x + dx * 0.4;
      const controlY1 = prev.y + dy * 0.4;
      const controlX2 = prev.x + dx * 0.7;
      const controlY2 = prev.y + dy * 0.7;
      path += ` C ${controlX1} ${controlY1}, ${controlX2} ${controlY2}, ${curr.x} ${curr.y}`;
    } else if (i === points.length - 1) {
      const prevPrev = points[i - 2];
      const dx1 = prev.x - prevPrev.x;
      const dy1 = prev.y - prevPrev.y;
      const dx2 = curr.x - prev.x;
      const dy2 = curr.y - prev.y;
      const controlX1 = prev.x + dx1 * 0.3;
      const controlY1 = prev.y + dy1 * 0.3;
      const controlX2 = prev.x + dx2 * 0.6;
      const controlY2 = prev.y + dy2 * 0.6;
      path += ` C ${controlX1} ${controlY1}, ${controlX2} ${controlY2}, ${curr.x} ${curr.y}`;
    } else {
      const prevPrev = points[i - 2];
      const next = points[i + 1];
      const dx1 = prev.x - prevPrev.x;
      const dy1 = prev.y - prevPrev.y;
      const dx2 = next.x - curr.x;
      const dy2 = next.y - curr.y;
      const controlX1 = prev.x + dx1 * 0.25;
      const controlY1 = prev.y + dy1 * 0.25;
      const controlX2 = curr.x - dx2 * 0.25;
      const controlY2 = curr.y - dy2 * 0.25;
      path += ` C ${controlX1} ${controlY1}, ${controlX2} ${controlY2}, ${curr.x} ${curr.y}`;
    }
  }

  return path;
}

/**
 * Scenario B: Previous timestamp is within history
 * Shows: Grey line before previous timestamp → green/red line after
 */
function generateScenarioBPath(
  history: PriceHistoryEntry[],
  options: EnhancedPathGenerationOptions
): PathSegment[] {
  const reversedHistory = [...history].reverse();
  const previousValue = options.previousValue!;
  const previousTs = previousValue.ts;

  // Find index where previous timestamp matches (or is closest to)
  let switchIndex = -1;
  for (let i = 0; i < reversedHistory.length; i++) {
    if (reversedHistory[i].ts === previousTs || new Date(reversedHistory[i].ts).getTime() >= new Date(previousTs).getTime()) {
      switchIndex = i;
      break;
    }
  }

  // If not found, use first index (all grey)
  if (switchIndex === -1) {
    switchIndex = 0;
  }

  // If switch is at the end, previous timestamp is at or after most recent history entry
  // Show colored trend based on history price change (oldest to most recent)
  if (switchIndex >= reversedHistory.length - 1) {
    const path = generateValueHistoryPath(history, options);
    if (!path) return [];

    // Determine color based on oldest vs most recent price in history
    const oldestPrice = reversedHistory[0].price;
    const mostRecentPrice = reversedHistory[reversedHistory.length - 1].price;
    const color = mostRecentPrice >= oldestPrice ? 'green' : 'red';

    return [{ path, color, isDotted: false }];
  }

  // Normalize all prices together
  const allPrices = reversedHistory.map(e => e.price);
  const minPrice = Math.min(...allPrices);
  const maxPrice = Math.max(...allPrices);
  const priceRange = maxPrice - minPrice;

  // Calculate dimensions
  const width = options.width - (options.padding * 2);
  const height = options.height - (options.padding * 2);
  const xStep = width / (reversedHistory.length - 1);

  // Split history into before and after switch
  const beforeHistory = reversedHistory.slice(0, switchIndex + 1);
  const afterHistory = reversedHistory.slice(switchIndex);

  // If no entries before switch, only show after
  if (beforeHistory.length < 2) {
    const afterPath = generatePathFromEntries(afterHistory, options, switchIndex, minPrice, maxPrice, priceRange, height, xStep);
    const mostRecentPrice = reversedHistory[reversedHistory.length - 1].price;
    const color = previousValue.price <= mostRecentPrice ? 'green' : 'red';
    return afterPath ? [{ path: afterPath, color, isDotted: false }] : [];
  }

  // Generate grey path for before
  const beforePath = generatePathFromEntries(beforeHistory, options, 0, minPrice, maxPrice, priceRange, height, xStep);

  // Generate colored path for after
  const afterPath = generatePathFromEntries(afterHistory, options, switchIndex, minPrice, maxPrice, priceRange, height, xStep);

  // Determine color based on previous vs most recent price
  const mostRecentPrice = reversedHistory[reversedHistory.length - 1].price;
  const color = previousValue.price <= mostRecentPrice ? 'green' : 'red';

  const segments: PathSegment[] = [];

  if (beforePath) {
    segments.push({ path: beforePath, color: 'grey', isDotted: false });
  }

  if (afterPath) {
    segments.push({ path: afterPath, color, isDotted: false });
  }

  return segments;
}

/**
 * Helper to generate path from a subset of history entries
 */
function generatePathFromEntries(
  entries: PriceHistoryEntry[],
  options: PathGenerationOptions,
  startIndex: number,
  minPrice: number,
  maxPrice: number,
  priceRange: number,
  height: number,
  xStep: number
): string {
  if (entries.length < 2) {
    return '';
  }

  const points = entries.map((entry, index) => {
    const x = options.padding + ((startIndex + index) * xStep);
    const normalizedPrice = priceRange > 0
      ? (entry.price - minPrice) / priceRange
      : 0.5;
    const y = options.padding + (height - (normalizedPrice * height));
    return { x, y };
  });

  if (points.length < 2) return '';
  if (points.length === 2) {
    return `M ${points[0].x} ${points[0].y} L ${points[1].x} ${points[1].y}`;
  }

  let path = `M ${points[0].x} ${points[0].y}`;

  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i];

    if (i === 1) {
      const dx = curr.x - prev.x;
      const dy = curr.y - prev.y;
      const controlX1 = prev.x + dx * 0.4;
      const controlY1 = prev.y + dy * 0.4;
      const controlX2 = prev.x + dx * 0.7;
      const controlY2 = prev.y + dy * 0.7;
      path += ` C ${controlX1} ${controlY1}, ${controlX2} ${controlY2}, ${curr.x} ${curr.y}`;
    } else if (i === points.length - 1) {
      const prevPrev = points[i - 2];
      const dx1 = prev.x - prevPrev.x;
      const dy1 = prev.y - prevPrev.y;
      const dx2 = curr.x - prev.x;
      const dy2 = curr.y - prev.y;
      const controlX1 = prev.x + dx1 * 0.3;
      const controlY1 = prev.y + dy1 * 0.3;
      const controlX2 = prev.x + dx2 * 0.6;
      const controlY2 = prev.y + dy2 * 0.6;
      path += ` C ${controlX1} ${controlY1}, ${controlX2} ${controlY2}, ${curr.x} ${curr.y}`;
    } else {
      const prevPrev = points[i - 2];
      const next = points[i + 1];
      const dx1 = prev.x - prevPrev.x;
      const dy1 = prev.y - prevPrev.y;
      const dx2 = next.x - curr.x;
      const dy2 = next.y - curr.y;
      const controlX1 = prev.x + dx1 * 0.25;
      const controlY1 = prev.y + dy1 * 0.25;
      const controlX2 = curr.x - dx2 * 0.25;
      const controlY2 = curr.y - dy2 * 0.25;
      path += ` C ${controlX1} ${controlY1}, ${controlX2} ${controlY2}, ${curr.x} ${curr.y}`;
    }
  }

  return path;
}
